<template>
  <div class="live-bids p-2" @keyup="keyUp" tabindex="0">
    <div class="bg-white d-flex flex-column h-100 pt-2 pr-2 pl-2 pb-2">
      <spinner :show="loading"/>
      <div v-if="!loading" class="main h-100 d-flex flex-column">
        <div class="content-header border-bottom mb-2 d-flex">
          <div class="d-inline-block">
            <h2 class="m-0">
              <router-link v-permission="'view_auction'" :to="{ name: 'sales.view', params: { id: saleInfo.uuid }}">
                <b>{{ saleInfo.sale_no }}</b>
              </router-link>
              - {{ saleInfo.title | masterLanguage }}
            </h2>
            <p class="m-0 mb-2">
              {{ saleInfo.start_date | toDateTime }}
              <template v-if="saleInfo.end_date">
                - {{ saleInfo.end_date | toDateTime }}
              </template>
              - <b>{{ saleInfo.department.name | masterLanguage }}</b>
            </p>
          </div>
          <div class="ml-auto pt-2">
            <b-form-group class="mb-0 d-inline-block mr-4">
              <b-form-radio-group v-model="inputSize" :options="sizeOptions" buttons
                                  button-variant="outline-primary"/>
            </b-form-group>

            <b-button variant="primary" class="d-inline-block mr-2" @click="onCreateInvoiceClick">
              <font-awesome-icon :icon="'file-invoice'"/>
              {{ $t('Create Invoice') }}
            </b-button>
            <a target="_blank">
              <b-button variant="primary" class="d-inline-block" target="_blank"
                        :to="{name: 'auctioneerConsole', params: {saleNo: saleInfo.sale_no}, query: {lot_no: activeLot.uuid}}">
                <font-awesome-icon :icon="'eye'"/>
                {{ $t('Open Auctioneer View') }}
              </b-button>
            </a>
          </div>
        </div>
        <div v-if='lots.length > 0' class="content-wrapper d-flex pb-3">
          <div class="left-column d-flex flex-column pr-2 border-right">
            <div class="lots">
              <div class="table-row" v-for="(lot,index) in lots" :key="lot.uuid"
                   :style="{
                    'padding': inputSize === 'sm' ? '0px 6px' : '4px 6px'
                   }"
                   @click="activateLot(lot)"
                   :class="{
                  active: activeLot === lot,
                  sold: lot.status === 'sold',
                  unsold: lot.status === 'unsold',
                  processed: lot.status === 'unsold' || lot.status === 'sold',
                }">
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell cell-status">
                  <font-awesome-icon v-if="lot.status === 'sold'" tabindex="-1" v-b-tooltip:html="$t('Sold')"
                                     class="text-success" :icon="'check-circle'"/>
                  <font-awesome-icon v-if="lot.status === 'unsold'" tabindex="-1" v-b-tooltip:html="$t('Unsold')"
                                     class="text-danger" :icon="'times'"/>
                </div>
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell">
                  <b-form-input :size="inputSize" tabindex="-1" class="input-lot" v-model="lot.lot_no" readonly/>
                </div>
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell">
                  <div>
                    <b-form-input ref="hammerPrice" class="input-hammer" v-model="lot.hammer_price"
                                  :disabled="lot.status === 'sold'" :placeholder="$t('Hammer Price')"
                                  type="number" :size="inputSize" @keydown="onInputKeyDown" @keyup.enter="_markSold"
                                  :state="isHammerPriceValid(index)" min="0"/>
                    <div class="invalid-feedback d-block hammer-error"
                         v-if="errors && errors[index] && errors[index].hammer_price">
                      {{ errors[index].hammer_price }}
                    </div>
                  </div>
                </div>
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell">
                  <div>
                    <b-form-input ref="paddleNumber" class="input-paddle" v-model="lot.paddle_number"
                                  :placeholder="$t('Paddle')" :size="inputSize" :disabled="lot.status === 'sold'"
                                  @keydown="onInputKeyDown" @keyup.enter="_markSold"
                                  :state="isPaddleNumberValid(index)"/>
                    <div class="invalid-feedback d-block" v-if="errors && errors[index] && errors[index].paddle_number">
                      {{ errors[index].paddle_number }}
                    </div>
                  </div>
                </div>
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell">
                  <b-form-input v-if="lot.buyer" tabindex="-1" class="input-invoice" :value="lot.invoice_no"
                                :placeholder="$t('Invoice')" :size="inputSize"
                                readonly/>
                </div>
                <div :class="{'table-cell-sm': inputSize === 'sm'}" class="table-cell">
                  <b-form-input v-if="lot.buyer" tabindex="-1" class="input-bidder" :value="lot.buyer.display_name"
                                :placeholder="$t('Buyer')" :size="inputSize"
                                readonly/>
                </div>
              </div>
            </div>
          </div>
          <div class="right-column pl-2">
            <live-bids-right-column :active-lot="activeLot" :sale-info="saleInfo"/>
          </div>
        </div>
        <div v-if='lots.length > 0' class="content-footer d-flex justify-content-between border-top pt-2 pb-0">
          <div>
            <b-button v-b-modal.helpModal variant="link">
              <font-awesome-icon :icon="'info-circle'"/>
              {{ $t('Quick Shortcuts') }}
            </b-button>
          </div>
          <div>
            <b-button variant="primary" @click="editLot" :class="$hasPermission('auction_results_auction') ? 'mr-5' : ''">
              {{ $t('Edit Lot') }}
            </b-button>
            <b-button variant="success" @click="_markSold" v-permission="'auction_results_auction'">
              {{ $t('Sold') }}
            </b-button>
            <b-button variant="danger" class="ml-2" @click="onUnsold" v-permission="'auction_results_auction'">
              {{ $t('Unsold') }}
            </b-button>
          </div>
        </div>
        <div v-if="lots.length === 0">
          {{ $t('Following sale has no lots') }}
        </div>
      </div>
    </div>
    <bidder-info-modal/>
    <shortcuts-modal :shortcuts="shortcuts"/>
    <unsold-lot-modal @success="onUnsoldSuccess"/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../core/components/common/Spinner";
  import ShortcutsModal from "./ShortcutsModal";
  import BidderInfoModal from "../bidders/BidderInfoModal";
  import {ALPHA_NUMERIC_REGEX} from "@/plugins/vee-validate";
  import LiveBidsRightColumn from "@/modules/live-bids/LiveBidsRightColumn";
  import {SOURCE_LIVE_BIDS} from "@/modules/constants";
  import UnsoldLotModal from "@/modules/live-bids/UnsoldLotModal";
  import LotsService from "@/modules/lots/LotsService";
  import helperService from "@/core/services/helper.service";

  const {mapActions, mapState} = createNamespacedHelpers('liveBids');
  const {mapActions: invoicesMapActions} = createNamespacedHelpers('invoices');
  const {mapActions: lotMapActions} = createNamespacedHelpers('lots');

  export default {
    name: "LiveBids",
    components: {UnsoldLotModal, LiveBidsRightColumn, BidderInfoModal, ShortcutsModal, Spinner},
    data() {
      return {
        sizeOptions: [
          {text: this.$i18n.t('Compressed'), value: 'sm'},
          {text: this.$i18n.t('Standard'), value: 'md'},
        ],
        inputSize: "md",
        saleNo: '',
        errors: [],
        shortcuts: [
          {key: 'enter', action: this.$t('Mark active lot as sold')},
          {key: 'ctrl + delete', action: this.$t('Mark active lot as unsold')},
          {key: 'up arrow', action: this.$t('Select previous lot')},
          {key: 'down arrow', action: this.$t('Select next lot')},
          {key: 'ctrl + b', action: this.$t('Open new Bidder modal')},
        ]
      }
    },
    computed: {
      ...mapState({
        loading: state => state.sale.loading,
        saleInfo: state => state.sale.saleInfo,
        activeLot: state => state.sale.activeLot,
        lots: state => state.sale.lots,
      }),
      hasError() {
        let activeLotIndex = this.lots.findIndex(l => l.uuid === this.activeLot.uuid);
        return !!(this.errors[activeLotIndex].paddle_number) || (this.activeLot.hammer_price < 0);
      }
    },
    watch: {},
    methods: {
      ...mapActions(['getSaleInfo', 'activateLot', 'markSold', 'markUnsold', 'showUnsoldLotModal']),
      ...invoicesMapActions({
        showInvoicesModal: 'showModal',
      }),
      ...lotMapActions({
        showReservationModal: 'showReservationModal',
        getReservationModalObject: 'getModalObject'
      }),
      onInputKeyDown(evt) {
        //prevent typing '-' to avoid negative numbers
        //109 - NumpadSubtract | 189 - Minus
        if (evt.which === 109 || evt.which === 189) {
          evt.preventDefault();
        }
        // 38 - up arrow | 40 - down array
        if (evt.which === 38) {
          evt.preventDefault();
        } else if (evt.which === 40) {
          evt.preventDefault();
        }
      },
      keyUp(evt) {
        // 73 - i | 66 - b | 38 - up arrow | 40 - down array | 46 delete
        if (evt.which === 38) {
          this.selectUpLot()
        } else if (evt.which === 40) {
          this.selectDownLot();
        } else if (evt.which === 46 && evt.ctrlKey === true) {
          evt.preventDefault();
          this.onUnsold();
        } else if (evt.which === 73 && evt.ctrlKey === true) {
          evt.preventDefault();
          this.viewInvoice();
        }
      },
      async _markSold() {
        if (!this.$hasPermission('auction_results_auction')) {
          this.$errorDialog(this.$i18n.t('You do not have permission to perform following action.'))
          return
        }

        if (this.activeLot.hammer_price && this.activeLot.paddle_number) {
          if (this.activeLot.status !== 'sold') {
            if (this.hasError) {
              return;
            }

            const reservation_proposals = (await LotsService.getLatestReservationProposals({stock_nos: [this.activeLot.stock_no]})).body
            if (reservation_proposals.length) {
              const confirm = await helperService.showProposalsConfirmationModal(reservation_proposals, this.$confirm, this.$router, this.$options.filters)
              if (!confirm) return
            }

            let response = await this.markSold({
              uuid: this.activeLot.uuid,
              hammer_price: this.activeLot.hammer_price,
              paddle_number: this.activeLot.paddle_number,
              status: 'sold'
            });

            if (response.success) {
              this.$toast('success', this.$i18n.t('Lot {lot} was marked as sold', {lot: this.activeLot.lot_no}));
            } else {
              if (response.body.non_field_errors) {
                this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t("Error while processing request"));
              } else if (response.body.hammer_price) {
                let activeLotIndex = this.lots.findIndex(l => l.uuid === this.activeLot.uuid);
                this.errors[activeLotIndex].hammer_price = response.body.hammer_price[0]
              }
            }
          }
          this.selectDownLot();
        }
      },

      onUnsoldSuccess() {
        setTimeout(() => this.selectDownLot(), 300);
      },
      async onUnsold() {
        if (!this.$hasPermission('auction_results_auction')) {
          this.$errorDialog(this.$i18n.t('You do not have permission to perform following action.'))
          return
        }

        if (this.activeLot.status === 'unsold') {
          this.$errorDialog('Following lot is already marked as unsold.')
          return
        }

        if (this.activeLot.status === 'sold') {
          const result = await this.$confirm(this.$i18n.t("This lot is already sold. Are you sure you want to mark it as unsold?"));
          if (result) {
            this.showUnsoldLotModal()
          }
        } else {
          this.showUnsoldLotModal()
        }
      },
      selectUpLot() {
        const index = this.lots.findIndex(l => l === this.activeLot);
        if (index === 0) {
          return;
        }
        this.activateLot(this.lots[index - 1]);
        this.$refs.hammerPrice[index - 1].$el.focus();
      },
      selectDownLot() {
        const index = this.lots.findIndex(l => l.uuid === this.activeLot.uuid);
        if (index === this.lots.length - 1) {
          return;
        }
        this.activateLot(this.lots[index + 1]);
        this.$refs.hammerPrice[index + 1].$el.focus();
      },
      focusCurrentLot() {
        const index = this.lots.findIndex(l => l.uuid === this.activeLot.uuid);
        if (this.$refs.hammerPrice[index]) {
          this.$refs.hammerPrice[index].$el.focus();
        }
      },
      isHammerPriceValid(index) {
        if (this.lots[index].hammer_price < 0) {
          if (this.errors.length)
            this.errors[index].hammer_price = this.$i18n.t("Specify correct value");
          return false
        } else if (this.lots[index].hammer_price < this.lots[index].reserve && this.lots[index].hammer_price && this.lots[index].status !== 'sold') {
          if (this.errors.length && !this.errors[index].hammer_price) {
            this.errors[index].hammer_price = this.$i18n.t("Hammer Price is less than Reserve");
          }
          return false
        } else {
          if (this.errors.length) {
            this.errors[index].hammer_price = '';
          }
          return null
        }
      },
      isPaddleNumberValid(index) {
        const isMatched = new RegExp(ALPHA_NUMERIC_REGEX).test(this.lots[index].paddle_number);
        if (isMatched) {
          if (this.errors.length) {
            this.errors[index].paddle_number = '';
          }
          return null
        } else {
          if (this.errors.length) {
            this.errors[index].paddle_number = this.$i18n.t("Specify correct value");
          }
          return false
        }
      },
      onCreateInvoiceClick() {
        this.showInvoicesModal({
          source: SOURCE_LIVE_BIDS,
          sale: this.saleInfo
        })
      },
      editLot() {
        this.showReservationModal({
          sale: this.saleInfo,
          source: SOURCE_LIVE_BIDS,
          disableFields: ['lot_no', 'withdraw']
        });
        this.getReservationModalObject({uuid: this.activeLot.reservation_uuid, source: SOURCE_LIVE_BIDS});
      }
    },
    async mounted() {
      let response = await this.getSaleInfo({saleNo: this.$route.params.saleNo});

      if (!response.success) {
        this.$router.push({name: 'startSale'});
      } else if (this.lots.length > 0) {
        this.focusCurrentLot();
      }
      this.errors = this.lots.map(() => this.errors = {hammer_price: '', paddle_number: ''});
    },
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables.scss";

  .live-bids {
    @include list-view-wrapper;

    .initial-page {
      height: 30em;
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-around;
      align-items: center;
      align-content: stretch;
    }
  }

  .content-wrapper {
    flex: 1;
    overflow: hidden;

    .left-column {
      height: 100%;
      min-width: 640px;
      width: 640px;
      overflow-y: hidden;
    }

    .right-column {
      flex: 1;
      overflow-x: hidden;
    }
  }

  .lots {
    overflow: auto;

    .table-row {
      display: flex;
      border: 4px solid transparent;


      &.processed {
        background-color: #e7e7e7;
      }

      &.active {
        background-color: #1fa6da61;
      }
    }

    .table-cell-sm {
      padding: 0 2px !important;
      &.cell-status {
        font-size: 15px !important;
      }

      .invalid-feedback {
        font-size: 9px !important;
      }
    }

    .table-cell {
      padding: 2px 4px;

      &.cell-status {
        min-width: 30px;
        font-size: 20px;
        text-align: center;
      }
    }

    .input-lot {
      width: 60px;
    }

    .input-hammer {
      width: 130px;
    }

    .hammer-error {
      width: 130px;
    }

    .input-paddle {
      width: 110px;
    }

    .input-invoice {
      width: 110px;
    }

    .input-bidder {
      width: 120px;
    }
  }

  .right-column /deep/ {
    .card-header {
      padding: 0.5rem 0.75rem;
      background-color: #f5f5f5;
    }

    .card-body {
      padding: 0.5rem 0.75rem;
    }
  }

  .card-commissions {
    table td {
      border-top: none;
    }
  }

  @media only screen and (max-width: 1200px) {
    .content-wrapper {
      flex-direction: column;
      align-content: stretch;

      .left-column {
        padding-right: 0 !important;
        padding-bottom: 1em;
        margin-bottom: 1em;
        border-right: none !important;
        flex: 1;
      }

      .right-column {
        flex: 1;
        padding-left: 0 !important;

        /deep/ .bidders-tab {
          height: 20em;
        }

        /deep/ .lot-overview-tab {
          height: 20em;
        }
      }
    }
  }
</style>
